import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";

const PREF_TOKEN_KEY = "pref_token";
const PREF_USER_KEY = "pref_user";

function getAuthHeader() {
  const token = localStorage.getItem(PREF_TOKEN_KEY);
  return { Authorization: `Bearer ${token}` };
}

export default function PrefAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("counselors"); // counselors | students
  const [counselors, setCounselors] = useState([]);
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", mobile: "", registration_number: "", branch: "AME Pune" });
  const [studentBranchFilter, setStudentBranchFilter] = useState("All");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem(PREF_USER_KEY) || "null");
    if (!u || u.role !== "ADMIN") { navigate("/engineering-admission/portal-login"); return; }
    setUser(u);
  }, [navigate]);

  const fetchCounselors = useCallback(async () => {
    try {
      const res = await api.get("/preferences/admin/counselors", { headers: getAuthHeader() });
      setCounselors(res.data);
    } catch { toast.error("Failed to load counselors"); }
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await api.get(`/preferences/students?search=${search}&branch=${studentBranchFilter}`, { headers: getAuthHeader() });
      setStudents(res.data);
    } catch { toast.error("Failed to load students"); }
  }, [search, studentBranchFilter]);

  useEffect(() => { if (user) fetchCounselors(); }, [user, fetchCounselors]);
  useEffect(() => { if (user && tab === "students") fetchStudents(); }, [user, tab, fetchStudents]);

  const handleSaveCounselor = async () => {
    setLoading(true);
    try {
      if (editItem) {
        await api.put(`/preferences/admin/counselors/${editItem.id}`, form, { headers: getAuthHeader() });
        toast.success("Counselor updated!");
      } else {
        await api.post("/preferences/admin/counselors", form, { headers: getAuthHeader() });
        toast.success("Counselor created!");
      }
      setShowModal(false); setEditItem(null); setForm({ name: "", email: "", password: "", branch: "AME Pune" });
      fetchCounselors();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error saving counselor");
    } finally { setLoading(false); }
  };

  const handleSaveStudent = async () => {
    setLoading(true);
    try {
      await api.post("/preferences/students", form, { headers: getAuthHeader() });
      toast.success("Student created!");
      setShowModal(false); setForm({ name: "", email: "", password: "", mobile: "", registration_number: "", branch: "AME Pune" });
      fetchStudents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error saving student");
    } finally { setLoading(false); }
  };

  const toggleCounselor = async (c) => {
    try {
      await api.put(`/preferences/admin/counselors/${c.id}`, { is_active: !c.is_active }, { headers: getAuthHeader() });
      toast.success(c.is_active ? "Counselor deactivated" : "Counselor activated");
      fetchCounselors();
    } catch { toast.error("Failed to toggle status"); }
  };

  const deleteCounselor = async (c) => {
    if (!window.confirm(`Delete counselor "${c.name}"?`)) return;
    try {
      await api.delete(`/preferences/admin/counselors/${c.id}`, { headers: getAuthHeader() });
      toast.success("Counselor deleted!");
      fetchCounselors();
    } catch { toast.error("Failed to delete"); }
  };

  const logout = () => {
    localStorage.removeItem(PREF_TOKEN_KEY);
    localStorage.removeItem(PREF_USER_KEY);
    navigate("/engineering-admission/portal-login");
  };

  if (!user) return null;

  return (
    <>
      <Helmet><title>Admin Dashboard | Preference Portal</title></Helmet>
      <style>{`
        .pad-page { min-height:100vh; background:#0f172a; color:#e2e8f0; font-family:'Inter',sans-serif; }
        .pad-topbar { display:flex; align-items:center; justify-content:space-between; padding:1rem 2rem; background:rgba(30,41,59,0.9); backdrop-filter:blur(12px); border-bottom:1px solid rgba(255,255,255,0.06); position:sticky; top:0; z-index:50; }
        .pad-topbar-left { display:flex; align-items:center; gap:1rem; }
        .pad-topbar-logo { width:40px; height:40px; border-radius:10px; background:linear-gradient(135deg,#0ea5e9,#0284c7); display:flex; align-items:center; justify-content:center; color:#fff; font-size:1rem; }
        .pad-topbar h2 { margin:0; font-size:1.15rem; color:#fff; font-weight:700; }
        .pad-topbar h2 span { color:#0ea5e9; }
        .pad-topbar-right { display:flex; align-items:center; gap:0.75rem; }
        .pad-user-chip { padding:0.4rem 1rem; background:rgba(14,165,233,0.1); border:1px solid rgba(14,165,233,0.25); border-radius:50px; color:#38bdf8; font-size:0.8rem; font-weight:600; }
        .pad-logout { padding:0.4rem 1rem; background:rgba(239,68,68,0.1); border:1px solid rgba(239,68,68,0.25); border-radius:8px; color:#f87171; cursor:pointer; font-size:0.8rem; font-weight:600; font-family:inherit; transition:all 0.2s; }
        .pad-logout:hover { background:rgba(239,68,68,0.2); }
        .pad-body { max-width:1100px; margin:0 auto; padding:2rem; }
        .pad-tabs { display:flex; gap:0.5rem; margin-bottom:1.5rem; }
        .pad-tab { padding:0.6rem 1.5rem; border-radius:10px; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.08); color:#64748b; cursor:pointer; font-weight:600; font-size:0.85rem; transition:all 0.2s; font-family:inherit; }
        .pad-tab:hover { color:#cbd5e1; border-color:rgba(255,255,255,0.15); }
        .pad-tab.active { background:rgba(14,165,233,0.12); border-color:#0ea5e9; color:#38bdf8; }
        .pad-toolbar { display:flex; align-items:center; justify-content:space-between; gap:1rem; margin-bottom:1.25rem; flex-wrap:wrap; }
        .pad-search { padding:0.55rem 1rem; border-radius:8px; background:rgba(0,0,0,0.2); border:1px solid rgba(255,255,255,0.08); color:#fff; outline:none; width:280px; font-family:inherit; }
        .pad-search:focus { border-color:#0ea5e9; }
        .pad-add-btn { padding:0.55rem 1.25rem; border-radius:10px; background:linear-gradient(135deg,#0ea5e9,#0284c7); color:#fff; border:none; cursor:pointer; font-weight:600; font-size:0.85rem; font-family:inherit; box-shadow:0 4px 12px rgba(14,165,233,0.3); transition:all 0.2s; }
        .pad-add-btn:hover { transform:translateY(-1px); box-shadow:0 6px 18px rgba(14,165,233,0.45); }
        .pad-table { width:100%; border-collapse:separate; border-spacing:0 6px; }
        .pad-table th { text-align:left; padding:0.6rem 1rem; color:#64748b; font-size:0.72rem; text-transform:uppercase; letter-spacing:1.5px; font-weight:700; }
        .pad-table td { padding:0.75rem 1rem; background:rgba(30,41,59,0.6); font-size:0.88rem; }
        .pad-table tr td:first-child { border-radius:10px 0 0 10px; }
        .pad-table tr td:last-child { border-radius:0 10px 10px 0; }
        .pad-status { display:inline-block; padding:0.2rem 0.65rem; border-radius:50px; font-size:0.72rem; font-weight:700; }
        .pad-status.active { background:rgba(16,185,129,0.12); color:#34d399; }
        .pad-status.inactive { background:rgba(239,68,68,0.12); color:#f87171; }
        .pad-action { padding:0.3rem 0.7rem; border-radius:6px; border:none; cursor:pointer; font-size:0.78rem; font-weight:600; margin-right:0.35rem; font-family:inherit; transition:all 0.15s; }
        .pad-action.edit { background:rgba(14,165,233,0.1); color:#38bdf8; }
        .pad-action.edit:hover { background:rgba(14,165,233,0.2); }
        .pad-action.toggle { background:rgba(245,158,11,0.1); color:#fbbf24; }
        .pad-action.toggle:hover { background:rgba(245,158,11,0.2); }
        .pad-action.delete { background:rgba(239,68,68,0.1); color:#f87171; }
        .pad-action.delete:hover { background:rgba(239,68,68,0.2); }
        .pad-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.6); backdrop-filter:blur(4px); z-index:100; display:flex; align-items:center; justify-content:center; }
        .pad-modal { background:#1e293b; border-radius:20px; padding:2rem; width:100%; max-width:420px; border:1px solid rgba(255,255,255,0.08); box-shadow:0 24px 48px rgba(0,0,0,0.5); }
        .pad-modal h3 { margin:0 0 1.5rem; color:#fff; font-size:1.2rem; font-weight:700; }
        .pad-modal label { display:block; margin-bottom:0.35rem; font-size:0.8rem; color:#94a3b8; font-weight:600; }
        .pad-modal input { width:100%; padding:0.6rem 0.85rem; border-radius:8px; background:rgba(0,0,0,0.25); border:1px solid rgba(255,255,255,0.08); color:#fff; margin-bottom:1rem; outline:none; font-size:0.9rem; box-sizing:border-box; font-family:inherit; }
        .pad-modal select.pad-select { width:100%; padding:0.6rem 0.85rem; border-radius:8px; background:rgba(0,0,0,0.25); border:1px solid rgba(255,255,255,0.08); color:#fff; margin-bottom:1rem; outline:none; font-size:0.9rem; box-sizing:border-box; font-family:inherit; appearance:none; }
        .pad-modal input:focus, .pad-modal select.pad-select:focus { border-color:#0ea5e9; }
        .pad-modal select.pad-select option { background:#1e293b; color:#fff; }
        .pad-modal-actions { display:flex; gap:0.75rem; justify-content:flex-end; margin-top:0.5rem; }
        .pad-modal-cancel { padding:0.5rem 1.25rem; border-radius:8px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); color:#94a3b8; cursor:pointer; font-weight:600; font-family:inherit; }
        .pad-modal-save { padding:0.5rem 1.25rem; border-radius:8px; background:#0ea5e9; border:none; color:#fff; cursor:pointer; font-weight:600; font-family:inherit; box-shadow:0 4px 12px rgba(14,165,233,0.3); }
        .pad-modal-save:disabled { opacity:0.5; cursor:not-allowed; }
        .pad-empty { text-align:center; padding:3rem; color:#475569; font-size:0.95rem; }
        .pad-branch-toggles { display:flex; gap:0.25rem; background:rgba(0,0,0,0.2); padding:0.25rem; border-radius:10px; border:1px solid rgba(255,255,255,0.05); }
        .pad-branch-btn { padding:0.45rem 1rem; border-radius:8px; border:none; cursor:pointer; font-weight:600; font-size:0.8rem; font-family:inherit; transition:all 0.2s; background:transparent; color:#64748b; }
        .pad-branch-btn:hover { color:#cbd5e1; }
        .pad-branch-btn.active { background:rgba(14,165,233,0.15); color:#38bdf8; border:1px solid rgba(14,165,233,0.25); }
      `}</style>

      <div className="pad-page">
        <div className="pad-topbar">
          <div className="pad-topbar-left">
            <div className="pad-topbar-logo"><i className="fas fa-shield-alt"></i></div>
            <h2><span>Admin</span> Dashboard</h2>
          </div>
          <div className="pad-topbar-right">
            <span className="pad-user-chip"><i className="fas fa-user" style={{marginRight:6}}></i>{user.name}</span>
            <button className="pad-logout" onClick={logout}><i className="fas fa-sign-out-alt" style={{marginRight:4}}></i>Logout</button>
          </div>
        </div>

        <div className="pad-body">
          <div className="pad-tabs">
            <button className={`pad-tab ${tab==='counselors'?'active':''}`} onClick={()=>setTab('counselors')}>
              <i className="fas fa-user-tie" style={{marginRight:6}}></i>Counselors
            </button>
            <button className={`pad-tab ${tab==='students'?'active':''}`} onClick={()=>setTab('students')}>
              <i className="fas fa-graduation-cap" style={{marginRight:6}}></i>Students
            </button>
          </div>

          {tab === "counselors" && (
            <>
              <div className="pad-toolbar">
                <span style={{color:'#94a3b8',fontSize:'0.85rem'}}>{counselors.length} counselor(s)</span>
                <button className="pad-add-btn" onClick={() => { setEditItem(null); setForm({name:"",email:"",password:"",branch:"AME Pune"}); setShowModal(true); }}>
                  <i className="fas fa-plus" style={{marginRight:6}}></i>Add Counselor
                </button>
              </div>
              {counselors.length === 0 ? (
                <div className="pad-empty"><i className="fas fa-user-tie" style={{fontSize:'2rem',marginBottom:'0.75rem',display:'block'}}></i>No counselors yet. Create one to get started.</div>
              ) : (
                <table className="pad-table">
                  <thead><tr><th>Name</th><th>Email</th><th>Branch</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {counselors.map(c => (
                      <tr key={c.id}>
                        <td style={{fontWeight:600,color:'#f1f5f9'}}>{c.name}</td>
                        <td>{c.email}</td>
                        <td><span style={{fontSize:'0.75rem',background:'rgba(14,165,233,0.15)',color:'#38bdf8',padding:'2px 6px',borderRadius:'4px'}}>{c.branch || "Unassigned"}</span></td>
                        <td><span className={`pad-status ${c.is_active?'active':'inactive'}`}>{c.is_active?'Active':'Inactive'}</span></td>
                        <td>
                          <button className="pad-action edit" onClick={() => { setEditItem(c); setForm({name:c.name,email:c.email,password:"",branch:c.branch||"AME Pune"}); setShowModal(true); }}><i className="fas fa-edit"></i></button>
                          <button className="pad-action toggle" onClick={() => toggleCounselor(c)}><i className={`fas ${c.is_active?'fa-ban':'fa-check'}`}></i></button>
                          <button className="pad-action delete" onClick={() => deleteCounselor(c)}><i className="fas fa-trash"></i></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {tab === "students" && (
            <>
              <div className="pad-toolbar" style={{alignItems:'center'}}>
                <div style={{display:'flex',gap:'1rem',alignItems:'center',flexWrap:'wrap'}}>
                  <input type="text" className="pad-search" placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)} />
                  <div className="pad-branch-toggles">
                    <button className={`pad-branch-btn ${studentBranchFilter === 'All' ? 'active' : ''}`} onClick={() => setStudentBranchFilter('All')}>All Branches</button>
                    <button className={`pad-branch-btn ${studentBranchFilter === 'AME Pune' ? 'active' : ''}`} onClick={() => setStudentBranchFilter('AME Pune')}>AME Pune</button>
                    <button className={`pad-branch-btn ${studentBranchFilter === 'AME Latur' ? 'active' : ''}`} onClick={() => setStudentBranchFilter('AME Latur')}>AME Latur</button>
                  </div>
                </div>
                <button className="pad-add-btn" onClick={() => { setEditItem(null); setForm({name:"",mobile:"",password:"",registration_number:"",branch:"AME Pune"}); setShowModal(true); }}>
                  <i className="fas fa-plus" style={{marginRight:6}}></i>New Student
                </button>
              </div>
              {students.length === 0 ? (
                <div className="pad-empty"><i className="fas fa-graduation-cap" style={{fontSize:'2rem',marginBottom:'0.75rem',display:'block'}}></i>No students found.</div>
              ) : (
                <table className="pad-table">
                  <thead><tr><th>Reg No.</th><th>Name</th><th>Mobile</th><th>Branch</th><th>Created</th></tr></thead>
                  <tbody>
                    {students.map(s => (
                      <tr key={s.id}>
                        <td style={{fontWeight:600,color:'#38bdf8',fontFamily:'monospace'}}>{s.registration_number}</td>
                        <td style={{fontWeight:600,color:'#f1f5f9'}}>{s.name}</td>
                        <td>{s.mobile}</td>
                        <td><span style={{fontSize:'0.75rem',background:'rgba(16,185,129,0.15)',color:'#34d399',padding:'2px 6px',borderRadius:'4px'}}>{s.branch || "Unassigned"}</span></td>
                        <td style={{color:'#64748b',fontSize:'0.82rem'}}>{new Date(s.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="pad-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="pad-modal" onClick={e => e.stopPropagation()}>
            <h3>{tab === "counselors" ? (editItem ? "Edit Counselor" : "New Counselor") : "New Student"}</h3>
            
            {tab === "counselors" ? (
              <>
                <label>Full Name</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Counselor name" />
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="counselor@example.com" />
                <label>Branch</label>
                <select value={form.branch} onChange={e => setForm({...form, branch: e.target.value})} className="pad-select">
                  <option value="AME Pune">AME Pune</option>
                  <option value="AME Latur">AME Latur</option>
                </select>
                <label>Password {editItem && "(leave blank to keep current)"}</label>
                <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" />
              </>
            ) : (
              <>
                <label>Registration Number</label>
                <input value={form.registration_number} onChange={e => setForm({...form, registration_number: e.target.value})} placeholder="e.g. REG2025001" />
                <label>Student Name</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Student full name" />
                <label>Mobile Number</label>
                <input type="tel" value={form.mobile} onChange={e => setForm({...form, mobile: e.target.value})} placeholder="10-digit mobile" />
                <label>Branch</label>
                <select value={form.branch} onChange={e => setForm({...form, branch: e.target.value})} className="pad-select">
                  <option value="AME Pune">AME Pune</option>
                  <option value="AME Latur">AME Latur</option>
                </select>
                <label>Password</label>
                <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" />
              </>
            )}

            <div className="pad-modal-actions">
              <button className="pad-modal-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="pad-modal-save" disabled={loading}
                onClick={tab === "counselors" ? handleSaveCounselor : handleSaveStudent}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
